// Timeline grid (SPEC §9.3): 15-minute rows, hour axis, hatched unavailable time, lessons in side-by-side lanes
// when they overlap, and a warning in the column header. Scrolls sideways inside its own box.
// Positions are multiples of the CSS variable --row-height, so a grid can be fitted to the screen by changing one
// value (fitTimelinesToScreen) without rebuilding it.

import { CELLS_PER_HOUR, formatCell, rangesOverlap, type CellRange } from "../time";
import { t } from "../i18n/i18n";
import { appendChildren, createElement, createLink } from "./dom";

/** The row height of grids that are not fitted to the screen. */
export const ROW_HEIGHT_PIXELS: number = 14;
/** Fitted rows never get smaller than this (text must stay readable) nor larger than the maximum. */
export const MIN_FITTED_ROW_PIXELS: number = 7;
export const MAX_FITTED_ROW_PIXELS: number = 18;
const DEFAULT_FIRST_CELL: number = 8 * CELLS_PER_HOUR;
const DEFAULT_LAST_CELL: number = 22 * CELLS_PER_HOUR;
const WHOLE_DAY_CELLS: number = 96;
const FIT_BOTTOM_MARGIN_PIXELS: number = 16;
const SCROLLBAR_ALLOWANCE_PIXELS: number = 14;

export interface TimelineLesson {
  range: CellRange;
  title: string;
  lines: string[];
  labels: string[];
  stateClass: string;
  route: string | null;
}

export interface TimelineBlock {
  range: CellRange;
  label: string;
}

export interface TimelineColumn {
  title: string;
  titleRoute: string | null;
  lessons: TimelineLesson[];
  unavailable: TimelineBlock[];
  /** Shown above the title on the first column of a group, e.g. the center over its classrooms. */
  groupTitle?: string;
}

/**
 * How the grid is laid out. "fit": rows sized to the window height and columns shrinking to the window width
 * (down to a minimum), for day views. "dense": like fit, with narrower columns and compact blocks, for many columns.
 */
export type TimelineLayout = "standard" | "fit" | "dense";

interface PlacedLesson {
  lesson: TimelineLesson;
  lane: number;
}

/** The whole grid for the columns. Call fitTimelinesToScreen() after inserting a fitted grid into the page. */
export function buildTimeline(columns: TimelineColumn[], layout: TimelineLayout = "standard"): HTMLElement {
  const visible: CellRange = findVisibleRange(columns);
  const scroller: HTMLDivElement = createElement("div", `timeline-scroll timeline-${layout}`);
  const grid: HTMLDivElement = createElement("div", "timeline");
  grid.style.setProperty("--timeline-columns", String(columns.length));
  grid.style.setProperty("--row-height", `${String(ROW_HEIGHT_PIXELS)}px`);
  grid.dataset.rows = String(visible.end - visible.start);
  grid.append(buildAxis(visible));
  columns.forEach((column: TimelineColumn, index: number): void => {
    const previous: TimelineColumn | undefined = columns[index - 1];
    const startsGroup: boolean = column.groupTitle !== undefined && previous?.groupTitle !== column.groupTitle;
    grid.append(buildColumn(column, visible, startsGroup));
  });
  scroller.append(grid);
  return scroller;
}

/**
 * Sizes the rows of every fitted grid on the page so the whole day fits between the grid's top and the bottom of
 * the window (SPEC §9.3). Called after rendering and on window resize.
 */
export function fitTimelinesToScreen(): void {
  for (const scroller of document.querySelectorAll<HTMLElement>(".timeline-fit, .timeline-dense")) {
    const grid: HTMLElement | null = scroller.querySelector<HTMLElement>(".timeline");
    const header: HTMLElement | null = scroller.querySelector<HTMLElement>(".timeline-column .timeline-header");
    if (grid === null) {
      continue;
    }

    const top: number = scroller.getBoundingClientRect().top + window.scrollY;
    const scrollbar: number = scroller.scrollWidth > scroller.clientWidth ? SCROLLBAR_ALLOWANCE_PIXELS : 0;
    const windowRoom: number = window.innerHeight - (header?.offsetHeight ?? 0) - scrollbar
      - FIT_BOTTOM_MARGIN_PIXELS;
    const rows: number = Number(grid.dataset.rows);
    // Content above the grid (e.g. a long "Needs attention" box) can leave too little room: then the page scrolls
    // and the grid fills one window height instead of shrinking to unreadable rows.
    const below: number = windowRoom - top;
    const available: number = below >= rows * MIN_FITTED_ROW_PIXELS ? below : windowRoom;
    grid.style.setProperty("--row-height", `${String(computeFittedRowHeight(available, rows))}px`);
  }
}

/** The row height that makes the rows fill the available height, kept between the readable limits. */
export function computeFittedRowHeight(availablePixels: number, rows: number): number {
  if (rows <= 0) {
    return ROW_HEIGHT_PIXELS;
  }

  const fitted: number = Math.floor(availablePixels / rows);
  return Math.min(MAX_FITTED_ROW_PIXELS, Math.max(MIN_FITTED_ROW_PIXELS, fitted));
}

/** 08:00–22:00, widened to whole hours around any lesson or partial unavailability outside it (SPEC §7.2). */
export function findVisibleRange(columns: TimelineColumn[]): CellRange {
  let first: number = DEFAULT_FIRST_CELL;
  let last: number = DEFAULT_LAST_CELL;
  for (const range of collectRanges(columns)) {
    first = Math.min(first, range.start);
    last = Math.max(last, range.end);
  }

  const firstHour: number = Math.floor(first / CELLS_PER_HOUR) * CELLS_PER_HOUR;
  const lastHour: number = Math.ceil(last / CELLS_PER_HOUR) * CELLS_PER_HOUR;
  return { start: firstHour, end: lastHour };
}

/** Gives each lesson a lane so overlapping lessons sit side by side; returns the lane count too. */
export function assignLanes(lessons: TimelineLesson[]): { placed: PlacedLesson[]; laneCount: number } {
  const sorted: TimelineLesson[] = [...lessons].sort((first, second): number => first.range.start - second.range.start);
  const laneEnds: number[] = [];
  const placed: PlacedLesson[] = sorted.map((lesson: TimelineLesson): PlacedLesson => {
    let lane: number = laneEnds.findIndex((laneEnd: number): boolean => laneEnd <= lesson.range.start);
    if (lane === -1) {
      lane = laneEnds.length;
    }

    laneEnds[lane] = lesson.range.end;
    return { lesson, lane };
  });
  return { placed, laneCount: Math.max(1, laneEnds.length) };
}

/** A length in rows as CSS, e.g. 8 rows → "calc(var(--row-height) * 8)". */
export function rowsToCss(rows: number): string {
  return `calc(var(--row-height) * ${String(rows)})`;
}

function collectRanges(columns: TimelineColumn[]): CellRange[] {
  const ranges: CellRange[] = [];
  for (const column of columns) {
    ranges.push(...column.lessons.map((lesson: TimelineLesson): CellRange => lesson.range));
    const partial: TimelineBlock[] = column.unavailable.filter(
      (block: TimelineBlock): boolean => block.range.end - block.range.start < WHOLE_DAY_CELLS);
    ranges.push(...partial.map((block: TimelineBlock): CellRange => block.range));
  }

  return ranges;
}

function buildAxis(visible: CellRange): HTMLElement {
  const axis: HTMLDivElement = createElement("div", "timeline-axis");
  axis.append(createElement("div", "timeline-header"));
  const body: HTMLDivElement = createBody(visible);
  for (let cell = visible.start; cell < visible.end; cell += CELLS_PER_HOUR) {
    const label: HTMLDivElement = createElement("div", "timeline-hour", formatCell(cell));
    label.style.top = rowsToCss(cell - visible.start);
    body.append(label);
  }

  axis.append(body);
  return axis;
}

function buildColumn(column: TimelineColumn, visible: CellRange, startsGroup: boolean): HTMLElement {
  const element: HTMLDivElement = createElement("div", startsGroup ? "timeline-column timeline-group-start"
    : "timeline-column");
  const lanes: { placed: PlacedLesson[]; laneCount: number } = assignLanes(column.lessons);
  element.append(buildColumnHeader(column, startsGroup, lanes.laneCount > 1));
  const body: HTMLDivElement = createBody(visible);
  for (const block of column.unavailable) {
    body.append(buildUnavailableBlock(block, visible));
  }

  for (const placed of lanes.placed) {
    body.append(buildLessonBlock(placed, lanes.laneCount, visible));
  }

  element.append(body);
  return element;
}

function buildColumnHeader(column: TimelineColumn, startsGroup: boolean, hasOverlap: boolean): HTMLElement {
  const header: HTMLDivElement = createElement("div", "timeline-header");
  if (column.groupTitle !== undefined) {
    // Every column carries its group so headers line up; only the first one shows it.
    const group: HTMLSpanElement = createElement("span", "timeline-group", startsGroup ? column.groupTitle : "");
    header.append(group);
  }

  const title: HTMLElement = column.titleRoute === null
    ? createElement("span", "", column.title)
    : createLink(column.titleRoute, column.title);
  title.title = column.groupTitle === undefined ? column.title : `${column.groupTitle} · ${column.title}`;
  header.append(title);
  if (hasOverlap) {
    header.append(createElement("span", "timeline-warning", "⚠ " + t("day.overlapWarning")));
  }

  return header;
}

function createBody(visible: CellRange): HTMLDivElement {
  const body: HTMLDivElement = createElement("div", "timeline-body");
  body.style.height = rowsToCss(visible.end - visible.start);
  return body;
}

function buildUnavailableBlock(block: TimelineBlock, visible: CellRange): HTMLElement {
  const clipped: CellRange = clip(block.range, visible);
  const element: HTMLDivElement = createElement("div", "timeline-unavailable", block.label);
  positionBlock(element, clipped, visible);
  element.title = block.label;
  return element;
}

function buildLessonBlock(placed: PlacedLesson, laneCount: number, visible: CellRange): HTMLElement {
  const lesson: TimelineLesson = placed.lesson;
  const element: HTMLElement = lesson.route === null
    ? createElement("div", `timeline-lesson ${lesson.stateClass}`)
    : createLink(lesson.route, "", `timeline-lesson ${lesson.stateClass}`);
  positionBlock(element, clip(lesson.range, visible), visible);
  element.style.left = `${String((placed.lane * 100) / laneCount)}%`;
  element.style.width = `${String(100 / laneCount)}%`;
  element.title = [lesson.title, ...lesson.lines, ...lesson.labels].join("\n");
  appendChildren(element, createElement("strong", "", lesson.title));
  for (const line of lesson.lines) {
    element.append(createElement("span", "timeline-line", line));
  }

  for (const label of lesson.labels) {
    element.append(createElement("span", "badge badge-flag", label));
  }

  return element;
}

function positionBlock(element: HTMLElement, range: CellRange, visible: CellRange): void {
  element.style.top = rowsToCss(range.start - visible.start);
  element.style.height = rowsToCss(range.end - range.start);
}

function clip(range: CellRange, visible: CellRange): CellRange {
  if (rangesOverlap(range, visible) === false) {
    return { start: visible.start, end: visible.start };
  }

  return { start: Math.max(range.start, visible.start), end: Math.min(range.end, visible.end) };
}
